import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css'; 
import { ReactSVG } from 'react-svg';
import {useHotkeys} from 'react-hotkeys-hook';

import { localStore } from '../../util/localStore';
import produce from 'immer';
import { uploadFile } from '../../modules/editor/service';
import { getFilesizeAsync, editContent, applyFilesize } from '../../modules/editor/actions';
import { RootState } from '../../modules';
import useAlert from '../../hooks/useAlert';
import { getPathAfterBuild } from '../../util/file';


type Props = {
	target: string;
	src: any;
	onClose: () => void;
};

function ImageCropper({ target, src, onClose }: Props) {
	const dispatch = useDispatch();
	const Alert = useAlert();

	const [img, setImg] = useState({} as any);
	const [cropper, setCropper] = useState({} as any);
	const [cropmode, setCropmode] = useState(false);
	const [undoStack, setUndoStack] = useState([]);
	const [redoStack, setRedoStack] = useState([]);
	const [showCropper, setShowCropper] = useState(false);
	const [saving, setSaving] = useState(false);

	const iconImage = (path) => {
		const context = require.context('../../assets/images/', true);
		return getPathAfterBuild(context, path);
	};

	const emailId = useSelector((state: RootState) => state.editor.email.id);
	const emailType = useSelector((state: RootState) => state.editor.email.type);
	const emailSubType = useSelector((state: RootState) => state.editor.email.subType);

	let testGroup: RootState['editor']['testGroup'] = undefined;
	if (emailType === 2 && emailSubType === 4) {
		testGroup = useSelector((state: RootState) => state.editor.testGroup);
	}

	useHotkeys('Esc', () => {
		close();
	});

	const actionList:any = {
		startCrop: () => {
			cropper.setDragMode('crop');
			cropper.crop();
			setCropmode(true);
		},
		endCrop: () => {			
			cropper.clear();
			cropper.setDragMode('none');
			setCropmode(false);
		},
		rotateClock: () => {
			cropper.rotate(90);
		},
		rotateAntiClock: () => {
			cropper.rotate(-90);
		},
		flipH: () => {
			let imgData = cropper.getImageData();
			cropper.scale(-(imgData.scaleX), imgData.scaleY);
		},
		flipV: () => {
			let imgData = cropper.getImageData();
			cropper.scale(imgData.scaleX, -(imgData.scaleY));
		},
		undo: () => {
			if (undoStack.length <= 1) {
				cropper.reset();
				cropper.clear();
				return;
			} 
			let last = undoStack.filter((item:any, index:number) => {
				return index === undoStack.length - 1;
			});
			cropper.setData(last[0]);
			setRedoStack(produce(redoStack, (draft:any[]) => {
				draft.push(last[0]);
			}));			
			setUndoStack(produce(undoStack, (draft:any[]) => {
				draft.pop();
			}))
		}, 
		redo: () => {			
			if (redoStack.length === 0) {
				return;
			} 
			let last = redoStack.filter((item: any, index: number) => {
				return index === redoStack.length - 1;
			});
			cropper.setData(last[0]);
			setRedoStack(produce(redoStack, (draft: any[]) => {
				draft.pop();
			}));
		},
		cropend: async () => {			
			let newImage = await cropper.getCroppedCanvas().toDataURL('image/jpeg');			
			localStore.set('stbImageTemp', newImage).then(() => {
				localStore.get('stbImageTemp').then((data: any) => {
					setImg(newImage);
					onCropperInit(cropper);					
				});
			});
		}
	}

	const action = (action:string) => {
		if (action !== 'undo' && action !== 'redo') {
			// history 저장/
			setUndoStack(
				produce( undoStack, (draft:any[]) => {
					draft.push(cropper.getData());
				})
			)
		}		
		// 리셋
		if (action !== 'cropend') {
			actionList.endCrop();	
		}
		// 실행
		return actionList[action]();
	}

	const [buttonList, setButtonList] = useState([
		{
			value: 'img-crop',
			name: '자르기',
			active: false,
			toggle: false,
			action: 'startCrop'
		},
		{
			value: 'img-rotate-clock',
			name: '오른쪽으로 회전',
			active: false,
			toggle: false,
			action: 'rotateClock'
		},
		{
			value: 'img-rotate-anticlock',
			name: '왼쪽으로 회전',
			active: false,
			toggle: false,
			action: 'rotateAntiClock'
		},
		{
			value: 'img-flip-h',
			name: '좌우 반전',
			active: false,
			toggle: true,
			action: 'flipH'
		},
		{
			value: 'img-flip-v',
			name: '상하 반전',
			active: false,
			toggle: true,
			action: 'flipV'
		},
		{
			value: 'undo',
			name: '실행 취소',
			active: false,
			toggle: false,
			action: 'undo'
		},
		{
			value: 'redo',
			name: '다시 실행',
			active: false,
			toggle: false,
			action: 'redo'
		},
	]);

	useEffect(() => {
		window.parent.postMessage({ msg: 'dialogShow' }, '*');		
		const TIMESTAMP = new Date().getTime();
		const srcPath = src.includes('?') ? `${src}&v=${TIMESTAMP}` : `${src}?v=${TIMESTAMP}`;
		toDataURL(srcPath, function (data: any) {
			setImg(data);
			setTimeout(() => {
				setShowCropper(true);						
			}, 500);
			
			// 최대 저장 용량을 확인
			// if (data.length > 5200000) {
			// 	Alert.error(Alert?.msg?.OVER_FILESIZE);
			// 	close();
			// 	return;
			// }

			// localStore.set('stbImageTemp', data).then(() => {
			// 	localStore.get('stbImageTemp').then((data: any) => {
			// 		setImg(data);
			// 		setTimeout(() => {
			// 			setShowCropper(true);						
			// 		}, 1000);
			// 	});
			// });	
		}, '');

		return () => {
			window.parent.postMessage({ msg: 'dialogHide' }, '*');
		}
	}, [])

	const toDataURL = (src: any, callback: any, outputFormat: any) => {		
		var img = new Image();
		img.crossOrigin = 'Anonymous';
		img.onload = function () {
			var t: any = this;
			var canvas: any = document.createElement('CANVAS');
			var ctx = canvas.getContext('2d');
			var dataURL;
			canvas.height = t.naturalHeight;
			canvas.width = t.naturalWidth;
			ctx.drawImage(this, 0, 0);
			dataURL = canvas.toDataURL(outputFormat);
			callback(dataURL);
		};

		img.src = src;
		if (img.complete || typeof img.complete === 'undefined') {
			img.src = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==";
			img.src = src;
		}
	}

	const close = () => {
		onClose();
		localStorage.removeItem('stbImageTemp');
	}

	const save = async () => {
		setSaving(true);
		setShowCropper(false);
		cropper.getCroppedCanvas().toBlob((blob:any) => {
			const formData = new FormData();
			formData.append('uploadfile', blob, 'croppedImage.png');
			uploadFile(emailId, formData, testGroup).then((res:any) => {
				const { totalSize, url } = res;
				if (url !== undefined) {
					dispatch(editContent({ target: target, data: url }));
					dispatch(applyFilesize(totalSize));
					setSaving(false);
				}
				close();
			})			
		});
	}

	const onCropperInit = (cropper_: any) => {
		setCropper(cropper_);
	}

	return (
		<>
		{	
		showCropper && (
			<div className={`image-cropper`}>
				<div className="image-cropper-btns">
					{
						buttonList.map((item:any, index:number) => (
							<button
								key={index}
								className={(cropmode && item.value === 'img-crop') ? 'btn active' : 'btn' }
								onClick={() => {action(item.action)}}
							>
								<ReactSVG src={iconImage(`./icon/toolbar/${item.value}.svg`)} height="16" />
							</button>
						))
					}
				</div>
				{
					showCropper && (
						<Cropper
							src={img}
							onInitialized={onCropperInit}
							dragMode="crop"
							autoCrop={false}
							autoCropArea={1.0}
							viewMode={2}
							checkCrossOrigin={false}
							minContainerWidth={660}
							minContainerHeight={440}
							style={{height: '500px'}} // max-heigth
						/>
					)
				}
				<div className="apply-btns">
					<button 
						onClick={close}
						className={saving ? 'btn medium tertiary disable' : 'btn medium tertiary'}>
						취소
					</button>
					<button 
						onClick={save}
						className={saving ? 'btn medium secondary disable' : 'btn medium secondary'}>
						저장하기
					</button>
				</div>
			</div>
		)
		}
		{
			!showCropper && (
				<div className="image-cropper-loading"></div>
			)
		}
		</>
	);
}

export default ImageCropper;